import { Col, Row, Spin } from 'antd';
import React, { useMemo, useRef, useState } from 'react';
import styles from './organisationDetails.module.scss';
import { CompanySetUpTabHash } from 'enums/CompanySetupEnums';
import DashboardContainer from 'views/Dashboard/DashboardContainer';
import TabsComponent from 'shared/components/TabsComponent';
import useFilters from 'shared/hooks/useFilters';
import {
  filterLocationParams,
  getModifiedValues,
  getOrganisationDetails,
} from './organisationDetails.util';
import OrganizationLocationDetails, {
  FetchLocationProps,
} from './LocationDetails';
import ViewOrganisationMap, {
  DeleteContent,
} from 'views/CompanySetup/OrganisationMap/ViewOrganisationMap';
import OrganisationService from 'services/OrganisationService/organisation.service';
import OrganizationDetailsHeader from './OrganizationDetailsHeader';
import OptionsDropdown from 'shared/components/OptionsDropdown';
import { useParams } from 'react-router-dom';
import useRedirect from 'shared/utils/useRedirect';
import {
  LocationParams,
  OrganisationDetailsEnum,
  OrganisationDetailsTabs,
} from 'constants/organizationDetails.constants';
import NewOrganisationModal from 'views/CompanySetup/OrganisationMap/EditOrganisationMap/SelectNewOrganisationModal/NewOrganisationModal';
import { OrganizationDetails } from 'models/OrganizationDetails/organizationDetails.model';
import { MapResetButton } from 'views/CompanySetup/OrganisationMap/OrganisationReactFlow/resetOrganizationMap';
import { OrganizationMapReset } from 'views/CompanySetup/OrganisationMap/OrganisationReactFlow';

const OrganisationDetailsPage = () => {
  const { id: organisationId } = useParams();
  const { redirectToCompanySetUp } = useRedirect();
  const { getParams, getHash, setParams } = useFilters();
  const {
    loading,
    fetchOrganizationDetails,
    organizationDetails,
    deleteOrganization,
    editOrganizationDetails,
  } = OrganisationService();

  const initialParams = getParams(LocationParams);
  const [selectedYear, setSelectedYear] = useState<string>(
    initialParams.year ?? ''
  );
  const [editVisible, setEditVisible] = useState<boolean>(false);
  const [mapCallBack, setMapCallBack] = useState<boolean>(false);

  const [filterParams, setFilterParams] = useState<FetchLocationProps>({
    year: initialParams.year ?? undefined,
    page: initialParams.page ? Number(initialParams.page) : 1,
    sortBy: initialParams.sortBy ?? '',
    sortDir: initialParams.sortDir ?? '',
    search: initialParams.search ?? '',
  });
  const mapRef = useRef<OrganizationMapReset>(null);

  const updateFilters = (params?: FetchLocationProps) =>
    setFilterParams(prev => {
      return { ...prev, ...params };
    });

  const data = useMemo(() => {
    return organizationDetails
      ? getOrganisationDetails(organizationDetails)
      : [];
  }, [organizationDetails]);

  const [activeTab, setActiveTab] = useState<string>(
    !!getHash() ? getHash() : CompanySetUpTabHash.ORG_MAP
  );

  const tabs = [
    {
      key: CompanySetUpTabHash.ORG_MAP,
      label: OrganisationDetailsTabs.ORG_MAP,
      children: selectedYear ? (
        <ViewOrganisationMap
          year={Number(selectedYear)}
          mapLoading={loading}
          isDetailsPage
          callBackTriggered={mapCallBack}
          organization={organizationDetails}
          mapRef={mapRef}
        />
      ) : null,
    },
    {
      key: CompanySetUpTabHash.LOCATIONS,
      label: OrganisationDetailsTabs.LOCATIONS,
      children: (
        <OrganizationLocationDetails
          filterParams={filterParams}
          updateFilters={updateFilters}
          activeTab={activeTab}
        />
      ),
    },
  ];

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    const filteredParams = filterLocationParams(filterParams);
    setParams(
      key === CompanySetUpTabHash.LOCATIONS
        ? filteredParams
        : { year: selectedYear },
      key
    );
  };

  const handleDelete = async () => {
    if (!organisationId) return;
    const response = await deleteOrganization(organisationId);
    if (!response) return;
    redirectToCompanySetUp(CompanySetUpTabHash.ORG_MAP, {
      year: selectedYear,
    })();
  };

  const handleEdit = async (values: OrganizationDetails): Promise<boolean> => {
    if (!organisationId) return false;
    const payload = getModifiedValues(values);
    const response = await editOrganizationDetails(
      organisationId,
      payload,
      Number(selectedYear)
    );
    if (!response) return false;
    setMapCallBack(prev => !prev);
    return true;
  };

  const toggleEdit = () => setEditVisible(prev => !prev);

  return (
    <DashboardContainer>
      <Spin spinning={loading} className={styles.loadingContainer}>
        <div className={styles.organisationDetailsWrapper}>
          <OrganizationDetailsHeader
            activeTab={activeTab}
            updateFilters={updateFilters}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            fetchOrganizationDetails={fetchOrganizationDetails}
            organizationDetails={organizationDetails}
          />
          <Row className={styles.organisationDetailsContent}>
            <Col span={24} className={styles.menu}>
              <OptionsDropdown
                deleteModalprops={{
                  deleteTitle: OrganisationDetailsEnum.DELETE_MODAL_TITLE,
                  onDelete: handleDelete,
                  loading: loading,
                  deleteContent: DeleteContent,
                }}
                isEdit
                editHandler={toggleEdit}
                className={styles.contextMenuWrapper}
              />
            </Col>
            {data.map((item, sectionIndex) => (
              <Col span={24} key={`section${sectionIndex}`}>
                <Row className={styles.organisationDetails}>
                  <Col span={24} className={styles.subTitle}>
                    {item.title}
                  </Col>
                  {item.orgData.map((details, detailIndex) => (
                    <Col
                      className={styles.detailsContainer}
                      key={`detail${detailIndex}`}>
                      <Row className={styles.detailsRow}>
                        <Col className={styles.detailsLabel}>
                          {details.label}
                        </Col>
                        <Col className={styles.detailsValue}>
                          {details.value}
                        </Col>
                      </Row>
                    </Col>
                  ))}
                </Row>
              </Col>
            ))}
          </Row>
          <Row>
            <Col span={24} className={styles.tabsContainer}>
              <TabsComponent
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                {...(activeTab === CompanySetUpTabHash.ORG_MAP && {
                  tabBarExtraContent: <MapResetButton mapRef={mapRef} />,
                })}
              />
            </Col>
          </Row>
          <NewOrganisationModal
            isOpen={editVisible}
            onClose={toggleEdit}
            isMainParent={
              organizationDetails?.organization?.organisationYearWise?.isParent
            }
            data={organizationDetails}
            handleSubmit={handleEdit}
            loading={loading}
          />
        </div>
      </Spin>
    </DashboardContainer>
  );
};

export default OrganisationDetailsPage;
